<template>
    <BaseView>
      <div class="folder-content content-panel">
        <div class="sessions-panel">
          <section role="region" title="Folder Title" aria-label="Folder Title" class="header">
            <Breadcrumb :home="home" :model="folders">
              <template #item="{ item, props }">
                <!-- If item has route, render as <router-link> -->
                <router-link v-if="item.route" :to="item.route" custom v-slot="{ href, navigate }">
                  <a :href="href" v-bind="props.action" @click="navigate">
                    {{ item.label }}
                  </a>
                </router-link>
                <!-- Otherwise just text -->
                <span v-else class="p-breadcrumb-item-text">{{ item.label }}</span>
              </template>
            </Breadcrumb>
            <Button
              v-if="!isArchived"
              variant="text"
              class="edit-folder-title-button"
              severity="danger"
              @click="openTitleChangeModal({ id: folderId, title: folderTitle, type: 'folder' })"
              @keydown="($event) => {
                if ($event.key === 'Enter') {
                  $event.preventDefault();
                  openTitleChangeModal({ id: folderId, title: folderTitle, type: 'folder' });
                }
              }"
              aria-label="Edit title"
            >
              <template #icon>
                <img
                  role="presentation"
                  :src="pencilWhite"
                  alt="Edit title"
                  class="edit-folder-title-icon" />
              </template>
            </Button>
          </section>

          <section role="region" title="Folder Contents">
            <div v-if="tableData.length === 0" class="subheader">
              This folder is currently empty.
            </div>
            
            <ItemList
            v-else-if="tableData.length > 0 && isDataLoaded"
            ref="folderItemList"
            :data="tableData"
            :columns="columns"
            :loader="loader"
            :sessionMenuProps="sessionMenuProps"
            :folderMenuProps="folderMenuProps"
            :openSession="open"
            :isArchive="isArchived"
            :scrollHeight="'100vh'"
            :lazyLoading="false"
            :storageKey="`folder-table-${folderId}`"
            />
          </section>
        </div>
      </div>
    </BaseView>
  <RequestNotesOrderForm
    :visible="orderFormVisible"
    :recordingTitle="sessionTitle"
    :durationHMS="durationHMS"
    :durationMilliseconds="durationMilliseconds"
    :recordingUploadId="recordingUploadId"
    :setOrderFormVisible="setOrderFormVisible"
    @hide="clearOrderFormValues"
    ref="orderForm"
  ></RequestNotesOrderForm>
  <TitleChangeModal
    :itemId="selectedId"
    :itemTitle="selectedTitle"
    :itemType="'folder'"
    :visible="titleChangeModalVisible"
    :setVisible="setTitleChangeModal"
    @updateTitleSuccessful="updateTitle"
  />
  <MoveSessionModal
    ref="moveSessionModalFolder"
    @reload-page="initPageData"
  />
  <RowActionMenubar
    v-if="folderItemList"
    :toggleSessionMenu="folderItemList?.toggleSessionMenu"
    :visible="rowActionVisible"
    @rename-item="openTitleChangeModal"
    @move-item="openMoveSessionModal"
  />
</template>
<script setup lang="ts">
import { ref, computed, useTemplateRef, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { previousRoute } from "@/router";
import BaseView from "../views/BaseView.vue";
import ItemList from "../components/Home/ItemList.vue";
import TitleChangeModal from "../components/TitleChangeModal.vue";
import MoveSessionModal from "../components/MoveSessionModal.vue";
import RequestNotesOrderForm from "../components/Orders/RequestNotesOrderForm.vue";
import RowActionMenubar from "../components/Home/RowActionMenubar.vue";
import { SessionWithOrdersAndType } from "@/types/session-interfaces";
import { OrderForm } from "@/types/order-interfaces";
import { ItemListColumn, ItemListMenuProp } from "@/types/itemlist-interfaces";
import { useTitleChangeModal } from "@/composables/useTitleChangeModal";
import { useOrderForm } from '@/composables/useOrderForm'
import { useRowActionVisibility } from "@/composables/useRowActionMenubar";
import { useSupplementaryUpload } from "@/composables/useSupplementaryUpload";
import pencilWhite from "@/assets/images/Pencil-white.svg";

const { triggerFileUpload } = useSupplementaryUpload();

const {
orderFormVisible,
sessionTitle,
durationHMS,
durationMilliseconds,
recordingUploadId,
requestNotes,
setOrderFormVisible,
} = useOrderForm();

const {
titleChangeModalVisible,
selectedTitle,
selectedId,
openTitleChangeModal,
setTitleChangeModal,
} = useTitleChangeModal();

const { rowActionVisible } = useRowActionVisibility();

const route = useRoute(),
    router = useRouter(),
    store = useStore();

const loader = ref<boolean>(false),
    folderTitle = ref<string>(""),
    isDataLoaded = ref<boolean>(false),
    isArchived = ref<boolean>(false),
    moveSessionModal = ref();

const orderForm = useTemplateRef<OrderForm>("orderForm"),
    folderItemList = useTemplateRef<typeof ItemList>("folderItemList"),
    moveSessionModalRef = useTemplateRef("moveSessionModalFolder");

const home = computed(() => {
if (previousRoute.value && 
    typeof previousRoute.value === 'object' && 
    'name' in previousRoute.value && 
    'path' in previousRoute.value) {
  return {
    label: previousRoute.value['name'],
    route: previousRoute.value['path'],
  };
} else {
  return {
    label: 'Home',
    route: '/home'
  };
}
});

const folders = computed(()  => [
{ label: folderTitle.value || route.query.title || 'Folder' },
]);

const columns = computed<ItemListColumn[]>(() => [
{  
  field: "title",
  header: "TITLE",
  sortable: true, 
  width: isArchived.value ? "30.44%" : "40.44%",
},
{
  field: "noteStatus",
  header: "NOTES STATUS",
  sortable: true,
  width: "14.58%",
},
{
  field: "uploads",
  sortable: true,
  sortFunction: (
    session1: SessionWithOrdersAndType,
    session2: SessionWithOrdersAndType
  ) => {
    const uploads1 = session1.uploads || [];
    const uploads2 = session2.uploads || [];
    return uploads1.length - uploads2.length;
  },
  header: "SUPPLEMENTARY FILES",
  width: "18.75%",
},
{
  field: "createdAt",
  header: "CREATED",
  sortable: true,
  width: "17.36%",
},
{
  field: "num",
  header: "", 
  icon: true, 
  width: isArchived.value ? "18.86%" : "4.86%",
},
]);

const sessionMenuProps = [
{
  label: "Open",
  icon: "itemlist-open-svg-icon",
  command: (rowData: SessionWithOrdersAndType) => {
    open(rowData);
  },
},
{
  label: "Request Professional Note",
  command: (rowData: SessionWithOrdersAndType) => {
    requestNotes(rowData);
  },
  icon: "itemlist-requestnotes-svg-icon",
  checkLabel: (rowData: SessionWithOrdersAndType) => {
    return rowData.noteStatus !== "no-value"
      ? "Notes Requested"
      : "Request Professional Notes";
  },
  checkVisible: (rowData: SessionWithOrdersAndType) => {
    return (
      !(
        rowData.uploads &&
        rowData.uploads[0] &&
        rowData.uploads[0].type === "mp-document"
      ) &&
      !(
        rowData.uploads &&
        rowData.uploads[0] &&
        rowData.uploads[0].type === "mp-instant-session"
      ) &&
      hasProNoteAccess.value
    );
  },
  checkDisable: (rowData: SessionWithOrdersAndType) => {
    return rowData.noteStatus !== "no-value";
  },
},
{
  label: "Add Supplementary Material",
  command: (rowData: SessionWithOrdersAndType) => {
    triggerFileUpload(rowData.id);
  },
  checkDisable: (rowData: SessionWithOrdersAndType) => { return rowData.uploads && rowData.uploads.filter((upload: any) => upload.type === "mp-supplementary").length > 4; },
  icon: "itemlist-upload-white-svg-icon",
},
{
  label: "Copy Session ID",
  command: (rowData: SessionWithOrdersAndType) => {
    copySessionId(rowData.id);
  },
  icon: "itemlist-copy-svg-icon",
},
{
  label: "Rename",
  icon: "itemlist-rename-svg-icon",
  command: (rowData: SessionWithOrdersAndType) => {
    console.log("[DEBUG]: TODO: OPEN RENAME MODAL HERE");
      openTitleChangeModal({id: rowData.id, title: rowData.title, type: "session"});
  },
},
];

const hasProNoteAccess = computed(() => {
  return store.getters['user/hasProNotesEnabled'];
});

const folderMenuProps: ItemListMenuProp[] = [];


function open(data: SessionWithOrdersAndType) {
store.commit("user/setSessionDetails", data);
router.push({
  name: "SessionInitializer",
});
}

function copySessionId(e: string) {
try {
  navigator.clipboard.writeText(e);
} catch (error) {
  console.log(error);
}
}

function clearOrderFormValues() {
if (
  orderForm.value &&
  orderForm.value.orderSubmitted &&
  orderForm.value.step &&
  orderForm.value.orderSubmissionErrorMessage
) {
  orderForm.value.orderSubmitted = false;
  orderForm.value.step = 0;
  orderForm.value.orderSubmissionErrorMessage = "";
}
}

// Computed properties
const folderId = computed(() => 
route.params.folderId as string
)

const folderIndex = computed(() => 
store.getters['itemList/getFolderIndexById'](route.params.folderId, 
  isArchived.value ? 'archivedItems' : 'allFolders'
)
)

const currentFolder = computed(() => {
if (folderIndex.value !== -1) {
  if (!isArchived.value){
    return store.state.itemList.allFolders[folderIndex.value] 
  }
  else {
    return store.state.itemList.archivedItems[folderIndex.value]
  }
}
else {
  return null
}
})

const tableData = computed(() => 
currentFolder.value?.sessions || []
)

const openMoveSessionModal = async () => {
await moveSessionModal.value?.show();
};

function updateTitle(title: string) {
folderTitle.value = title;
}

const initPageData = async  () => {
  if (currentFolder.value) {
    if (isArchived.value){
      await store.dispatch('itemList/loadAndProcessFolder', {folderId: folderId.value, array: 'archivedItems'})
    }
    else {
      await store.dispatch('itemList/loadAndProcessFolder', {folderId: folderId.value, array: 'allFolders'})
    }
    isDataLoaded.value = true
  } 
}

onMounted(async () => {
loader.value = true;
// folderId.value = route.params.folderId as string;
// folderTitle.value = route.query.title as string;
isArchived.value = route.query.isArchived === "true";

updateTitle(route.query.title as string || "Folder");

if (currentFolder.value) {
  if (isArchived.value){
    await store.dispatch('itemList/loadAndProcessFolder', {folderId: folderId.value, array: 'archivedItems'})
  }
  else {
    await store.dispatch('itemList/loadAndProcessFolder', {folderId: folderId.value, array: 'allFolders'})
  }
  isDataLoaded.value = true
} 
moveSessionModal.value = moveSessionModalRef.value;
loader.value = false;
});
</script>

<style lang="scss" scoped>
.folder-content {
width: 100%;
height: 100%;
display: flex;
flex-direction: column;
padding: 4rem 5.6rem;
overflow: auto;

.sessions-panel {
  .header {
    display: flex;
    align-items: center;
    .edit-folder-title-icon {
      width: 2.4rem;
      height: 2.4rem;
      margin-bottom: 0.4rem;
    }
  }
  .subheader {
    @include section-sub-header;
    font-size: 1.8rem;
    font-weight: bold;
    text-align: center;
      padding-top: 4rem;
  }
}
}

.v-popper__popper .v-popper__inner {
font-size: 1.2rem;
padding: 12px;
}
</style>